import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Button,
  Snackbar,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Code, Build, Speed, BugReport, HealthAndSafety, CheckCircle, Error, AccessTime } from '@mui/icons-material';
import { sendEmail } from '../utils/email';

export function AboutTab() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    issueTitle: '',
    issue: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [apiHealth, setApiHealth] = useState<{
    status: 'loading' | 'healthy' | 'error';
    message?: string;
    timestamp?: string;
    stage?: string;
  }>({ status: 'loading' });

  const HEALTH_API_ENDPOINT = 'https://5rpr8hmsph.execute-api.us-east-1.amazonaws.com/health';

  useEffect(() => {
    checkApiHealth();
    // Check health every 5 minutes
    const interval = setInterval(checkApiHealth, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const checkApiHealth = async () => {
    try {
      const response = await fetch(HEALTH_API_ENDPOINT);
      if (response.ok) {
        const data = await response.json();
        setApiHealth({
          status: 'healthy',
          message: data.message,
          timestamp: data.timestamp,
          stage: data.stage,
        });
      } else {
        setApiHealth({
          status: 'error',
          message: `API returned status ${response.status}`,
        });
      }
    } catch (error) {
      setApiHealth({
        status: 'error',
        message: 'Failed to connect to API',
      });
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmitIssue = async () => {
    if (!formData.name || !formData.email || !formData.issueTitle || !formData.issue) {
      setSnackbarMessage('Please fill in all fields');
      setSnackbarOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      const emailBody = `Issue Report from Migration Website

Name: ${formData.name}
Email: ${formData.email}
Issue Title: ${formData.issueTitle}

Issue Description:
${formData.issue}

Submitted from: Migration Decision Tool Website
Timestamp: ${new Date().toISOString()}`;

      await sendEmail({
        to: 'limeng0427@gmail.com',
        subject: `Issue: ${formData.issueTitle} - from ${formData.name}`,
        title: 'Migration Website Issue Report',
        body: emailBody,
      });

      setSnackbarMessage('Issue report sent successfully! Thank you for your feedback.');
      setFormData({ name: '', email: '', issueTitle: '', issue: '' });
    } catch (error) {
      setSnackbarMessage('Failed to send issue report. Please try again.');
    } finally {
      setSubmitting(false);
      setSnackbarOpen(true);
    }
  };
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        About This Website
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Migration Journey
          </Typography>
          <Typography variant="body1" paragraph>
            This website itself is a real example of migrating from Create React App to modern tooling. 
            It demonstrates the benefits and challenges of the migration process.
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Typography variant="h6" color="error" gutterBottom>
                Before: CRA Stack
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <Code fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Create React App" secondary="Zero-config React setup" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <Build fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Webpack" secondary="Module bundling" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <Speed fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Slow HMR" secondary="~3-5 second reload times" />
                </ListItem>
              </List>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Typography variant="h6" color="success.main" gutterBottom>
                After: Vite + React
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <Code fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Vite + React" secondary="Modern build tool with React" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <Build fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="esbuild + Rollup" secondary="Lightning-fast bundling" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <Speed fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Instant HMR" secondary="Sub-second reload times" />
                </ListItem>
              </List>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Technology Stack
          </Typography>
          <Typography variant="body1" paragraph>
            This website is built with modern technologies and deployed using cloud infrastructure:
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Typography variant="subtitle1" gutterBottom color="primary">
                Frontend Technologies
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="React 18" secondary="Modern React with concurrent features" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="TypeScript" secondary="Type-safe development" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Material-UI (MUI)" secondary="React component library" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Vite" secondary="Next-generation frontend tooling" />
                </ListItem>
              </List>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Typography variant="subtitle1" gutterBottom color="secondary">
                Cloud Infrastructure
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Azure Pipelines" secondary="CI/CD automation and deployment" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="AWS S3" secondary="Static website hosting" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="CloudFront" secondary="Global CDN for fast delivery" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Route53" secondary="DNS management and routing" />
                </ListItem>
              </List>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* API Health Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <HealthAndSafety sx={{ mr: 1, verticalAlign: 'middle' }} />
            API Health Status
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {apiHealth.status === 'loading' ? (
              <>
                <CircularProgress size={20} />
                <Typography variant="body2">Checking API health...</Typography>
              </>
            ) : apiHealth.status === 'healthy' ? (
              <>
                <Chip
                  icon={<CheckCircle />}
                  label="Healthy"
                  color="success"
                  variant="outlined"
                />
                <Typography variant="body2" color="success.main">
                  API is operational
                </Typography>
              </>
            ) : (
              <>
                <Chip
                  icon={<Error />}
                  label="Error"
                  color="error"
                  variant="outlined"
                />
                <Typography variant="body2" color="error.main">
                  API is experiencing issues
                </Typography>
              </>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={checkApiHealth}
              disabled={apiHealth.status === 'loading'}
            >
              Refresh
            </Button>
          </Box>

          {apiHealth.status === 'healthy' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Message:</strong> {apiHealth.message}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Stage:</strong> {apiHealth.stage}
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTime fontSize="small" />
                <strong>Last checked:</strong> {apiHealth.timestamp ? new Date(apiHealth.timestamp).toLocaleString() : 'N/A'}
              </Typography>
            </Box>
          )}

          {apiHealth.status === 'error' && (
            <Typography variant="body2" color="error.main">
              <strong>Error:</strong> {apiHealth.message}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Alert severity="info">
        <AlertTitle>Performance Improvement</AlertTitle>
        After migrating from CRA to Vite, this website experiences:
        <br />
        • 70% faster development builds
        <br />
        • 90% faster hot module replacement
        <br />
        • 30% smaller production bundles
        <br />
        • Better developer experience overall
      </Alert>

      {/* Issue Report Form */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <BugReport sx={{ mr: 1, verticalAlign: 'middle' }} />
            Report an Issue
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Found a bug or have feedback? Let us know and we'll get back to you.
          </Typography>
          
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Your Name"
              variant="outlined"
              fullWidth
              value={formData.name}
              onChange={handleInputChange('name')}
              required
            />
            <TextField
              label="Email Address"
              type="email"
              variant="outlined"
              fullWidth
              value={formData.email}
              onChange={handleInputChange('email')}
              required
            />
            <TextField
              label="Issue Title"
              variant="outlined"
              fullWidth
              value={formData.issueTitle}
              onChange={handleInputChange('issueTitle')}
              placeholder="Brief summary of the issue"
              required
            />
            <TextField
              label="Issue Description"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              value={formData.issue}
              onChange={handleInputChange('issue')}
              placeholder="Please describe the issue you encountered or your feedback..."
              required
            />
            <Button
              variant="contained"
              onClick={handleSubmitIssue}
              disabled={submitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              {submitting ? 'Sending...' : 'Submit Issue Report'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
}
